/** biome-ignore-all lint/a11y/useAriaPropsSupportedByRole: not needed */
/** biome-ignore-all lint/a11y/noLabelWithoutControl: it's inside the component */

import { type FC, Fragment, type ReactNode, useMemo, useState } from 'react';

import cn from 'classnames';

import { groupByField } from '@core/Utils/GroupByField/GroupByField';
import Checkbox from '@gds/Checkbox/Checkbox';
import { Typography } from '@gds/Typography/Typography';

import type { Item, MultiSelectProps } from './MultiSelect.entity';

import styles from './MultiSelect.module.css';

export const MultiSelect: FC<MultiSelectProps> = ({
  items,
  onSelect,
  title,
  classes,
  noResultText,
  dataTestId = 'multi-checkbox',
  isGrouped = false,
  groupBy = 'label',
  shouldOrder,
  keyAsTitle = false,
  groupName,
  applyButton,
  hideScrollbar,
}) => {
  const [pendingSelections, setPendingSelections] = useState<Record<string, boolean>>({});

  const groups = useMemo(
    () => groupByField(items, groupBy as keyof Item, isGrouped, keyAsTitle, shouldOrder),
    [items, isGrouped],
  );

  const makeTitle = (label: ReactNode) => (
    <Typography
      variant="subheading1"
      Component="p"
      className={styles.heading}
      data-test-id="letter"
    >
      {label}
    </Typography>
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();
    const { value, checked: isChecked } = event.target;

    if (applyButton) {
      setPendingSelections(prev => ({ ...prev, [value]: isChecked }));
    }

    onSelect(value, isChecked);
  };

  return (
    <div data-test-id={dataTestId}>
      {title &&
        (typeof title === 'string' ? (
          <Typography variant="subheading3" Component="p" className={cn(classes?.title)}>
            {title}
          </Typography>
        ) : (
          title
        ))}
      {items.length === 0 ? (
        <Typography variant="body4" Component="p">
          {noResultText}
        </Typography>
      ) : null}
      <ul
        tabIndex={-1}
        className={`${styles.listbox} ${classes?.listbox ?? ''} ${
          applyButton && styles.listboxWithApplyButton
        } ${hideScrollbar && styles.hideScrollbar}`}
      >
        {groups.map((group, index) => (
          <Fragment key={index + 1}>
            {group.title && makeTitle(group.title)}
            {group.items.map(({ item }) => {
              const key = groupName ? `${groupName}-${item.id}` : item.id;

              const classNames = cn(styles.labelWrapper, {
                [styles.active]: item.selected,
                [classes?.label as string]: !!classes?.label,
              });

              const isChecked = applyButton
                ? (pendingSelections?.[item.id] ?? item.selected)
                : item.selected;

              return (
                <li key={key} aria-selected={true}>
                  <label aria-labelledby={key} className={classNames}>
                    <Checkbox
                      id={key}
                      onChange={handleChange}
                      disabled={item.disabled}
                      value={item.id}
                      checked={!!isChecked}
                      tabIndex={0}
                      className={styles.checkbox}
                      name={key}
                    />
                    {item.renderLabel ? item.renderLabel : <span>{item.label}</span>}

                    {item.count && (
                      <Typography Component="span" className={styles.count} variant="caption2">
                        ({item.count})
                      </Typography>
                    )}
                  </label>
                </li>
              );
            })}
          </Fragment>
        ))}
      </ul>
    </div>
  );
};
